import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Package, Loader2 } from 'lucide-react';
import { useOrder } from '@/hooks/useProducts';
import { formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import PostPurchaseUpsell from '@/components/upsell/PostPurchaseUpsell';
import { useEffect } from 'react';
import { trackOrderCompleted } from '@/lib/integrations/storefront-events';

export default function OrderConfirmation() {
  const { t } = useTranslation('checkout');
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useOrder(id || '');

  useEffect(() => {
    if (order?.id) {
      trackOrderCompleted({
        id: order.id,
        order_number: order.order_number ?? null,
        total: Number(order.total ?? 0),
        tax: Number(order.tax_amount ?? 0),
        shipping: Number(order.shipping_cost ?? 0),
        coupon: order.coupon_code ?? null,
        email: order.email ?? null,
        items: (order.items ?? []).map((i: any) => ({
          product_id: String(i.product_id ?? ''),
          variant_id: String(i.variant_id ?? ''),
          product_name: String(i.product_name ?? ''),
          variant_name: i.variant_name ?? null,
          price: Number(i.unit_price ?? 0),
          quantity: Number(i.quantity ?? 1),
          sku: i.sku ?? null,
          image_url: i.image_url ?? null,
        })),
      });
    }
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-soft flex items-center justify-center p-4">
        <Loader2
          className="h-8 w-8 animate-spin text-sage-600"
          aria-label={t('confirmation.loadingAriaLabel')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-soft flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl border border-line p-8 max-w-lg w-full text-center">
        <CheckCircle className="h-16 w-16 text-success-500 mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-display font-bold text-ink mb-2">
          {t('confirmation.confirmedHeading')}
        </h1>
        <p className="text-ink-soft mb-1">{t('confirmation.thankYou')}</p>
        {order && (
          <p className="text-sm text-ink-muted mb-6">
            {t('confirmation.orderNumberLabel')}{' '}
            <span className="font-semibold text-ink-soft">{order.order_number}</span>
          </p>
        )}
        {order && (
          <div className="text-left border border-line rounded-lg p-4 mb-6">
            <h2 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <Package className="h-4 w-4" aria-hidden="true" /> {t('confirmation.detailsHeading')}
            </h2>
            {order.items?.map((item: any) => (
              <div key={item.id} className="flex justify-between text-sm py-1">
                <span className="text-ink-soft">
                  {t('confirmation.itemRow', { name: item.product_name, quantity: item.quantity })}
                </span>
                <span className="font-medium">{formatCurrency(Number(item.total))}</span>
              </div>
            ))}
            <div className="border-t mt-2 pt-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-muted">{t('confirmation.subtotal')}</span>
                <span>{formatCurrency(Number(order.subtotal ?? order.total))}</span>
              </div>
              {Number(order.shipping_cost) > 0 ? (
                <div className="flex justify-between">
                  <span className="text-ink-muted">{t('confirmation.shipping')}</span>
                  <span>{formatCurrency(Number(order.shipping_cost))}</span>
                </div>
              ) : order.shipping_cost != null ? (
                <div className="flex justify-between">
                  <span className="text-ink-muted">{t('confirmation.shipping')}</span>
                  <span className="text-success-700">{t('confirmation.shippingFree')}</span>
                </div>
              ) : null}
              {order.tax_amount != null && Number(order.tax_amount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-ink-muted">{t('confirmation.tax')}</span>
                  <span>{formatCurrency(Number(order.tax_amount))}</span>
                </div>
              )}
              {order.discount_amount != null && Number(order.discount_amount) > 0 && (
                <div className="flex justify-between text-success-700">
                  <span>{t('confirmation.discount')}</span>
                  <span>{t('confirmation.discountValue', { amount: formatCurrency(Number(order.discount_amount)) })}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-1 border-t border-line">
                <span>{t('confirmation.total')}</span>
                <span>{formatCurrency(Number(order.total))}</span>
              </div>
            </div>
          </div>
        )}
        <p className="text-sm text-ink-muted mb-6">
          {t('confirmation.willEmailDetails')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/account/orders"><Button variant="outline">{t('confirmation.viewOrders')}</Button></Link>
          <Link to="/shop"><Button>{t('confirmation.continueShoppingButton')}</Button></Link>
        </div>
      </div>

      {id && <PostPurchaseUpsell orderId={id} />}
    </div>
  );
}
